import type { CalendarApi, CalendarRef, DateInput } from '@fullcalendar/react';
import { useCallback, useMemo, useRef } from 'react';
import { getFixedMonthVisibleRange } from '../lib/calendar';
import { ABS_DAY_VIEW, ABS_MONTH_VIEW } from '../lib/constants';
import { userResourceId } from '../lib/resources';
import { createResourceGroups, type ResourceGroups } from './groups';

/**
 * Everything the application asks the renderer to *do*.
 *
 * Named operations rather than a calendar handle: the DOM queries, the
 * synthetic clicks, the layout timers and the scroll restoration all live
 * behind these, so nothing above this line holds a node or a FullCalendar
 * object. That is what lets the renderer be replaced without draft state,
 * selection state or the query layer moving at all.
 */
export type CalendarController = {
  groups: ResourceGroups;
  attach: (instance: CalendarRef | null) => void;
  next: () => void;
  prev: () => void;
  today: () => void;
  getDate: () => Date | null;
  select: (...args: Parameters<CalendarApi['select']>) => void;
  unselect: () => void;
  changeView: (...args: Parameters<CalendarApi['changeView']>) => void;
  gotoDate: (...args: Parameters<CalendarApi['gotoDate']>) => void;
  setMonthView: (year: number, month: number) => void;
  setMonthViewFrom: (startDate: Date, dayCount?: number) => void;
  setDayView: (date: DateInput) => void;
  setOption: (...args: Parameters<CalendarApi['setOption']>) => void;
  scrollToResource: (userId: number) => void;
  /**
   * Bring an employee's row into view, expanding its group first if the row is
   * not drawn yet.
   *
   * Takes the group's *value* -- its identity, not its label. Which group
   * somebody belongs to is the application's business; finding and opening the
   * row that says so is the renderer's.
   */
  revealResource: (userId: number, groupValue: string | null) => void;
};

/**
 * One controller for the page. Created by the provider, read from context.
 *
 * Not per caller: it holds the ref the calendar attaches itself to, and two
 * callers with two refs would leave one of them talking to nothing.
 */
export default function useCalendarController(): CalendarController {
  const calendarRef = useRef<CalendarRef | null>(null);
  const getApi = useCallback(() => calendarRef.current?.getApi() ?? null, []);

  return useMemo<CalendarController>(() => {
    // A 31-day window starting at `startDate` unless a caller needs a longer
    // one -- a run that will not fit a month is shown in full and scrolled,
    // rather than cut off where somebody might decide on the part they can
    // see. The range is applied imperatively because the view is duration-free.
    const showMonthFrom = (startDate: Date, dayCount?: number) => {
      const { start, end } = getFixedMonthVisibleRange(startDate, dayCount);
      getApi()?.changeView(ABS_MONTH_VIEW, startDate);
      getApi()?.setOption('visibleRange', { start, end });
    };
    const scrollToResource = (userId: number) => {
      const resourceId = userResourceId(userId);
      if (!resourceId) return;
      requestAnimationFrame(() => {
        const row = document.querySelector(
          `[data-resource-id="${resourceId}"]`,
        ) as HTMLElement | null;
        row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    };
    const groups = createResourceGroups();
    return {
      groups,
      attach: (instance) => {
        calendarRef.current = instance;
      },
      next: () => getApi()?.next(),
      prev: () => getApi()?.prev(),
      today: () => getApi()?.today(),
      getDate: () => getApi()?.getDate() ?? null,
      select: (...args) => getApi()?.select(...args),
      unselect: () => getApi()?.unselect(),
      changeView: (...args) => getApi()?.changeView(...args),
      gotoDate: (...args) => getApi()?.gotoDate(...args),
      setMonthView: (year, month) => showMonthFrom(new Date(year, month, 1)),
      setMonthViewFrom: showMonthFrom,
      setDayView: (date) => getApi()?.changeView(ABS_DAY_VIEW, date),
      setOption: (...args) => getApi()?.setOption(...args),
      scrollToResource,
      revealResource: (userId, groupValue) => {
        const resourceId = userResourceId(userId);
        if (!resourceId) return;

        // Request expansion even if the resource is still drawn: an initial
        // collapse may be queued but not committed yet.
        if (groupValue) groups.setExpanded(groupValue, true);
        requestAnimationFrame(() => {
          scrollToResource(userId);
        });
      },
    };
  }, [getApi]);
}
